import { useCallback, useState, type ReactNode } from 'react';

import { ariaSort, prossimoOrdinamento, type Ordinamento } from './ordinamento.ts';

/**
 * Lo stato dell'ordinamento di una tabella.
 *
 * ⚠️ Vive nella pagina e **non** nell'indirizzo: ricaricando si torna
 * all'ordine del server, ed è voluto. L'ordinamento è un modo di guardare, non
 * un filtro — quelli stanno nella query perché vanno condivisi con un
 * collegamento, questo no.
 */
export function useOrdinamento(): {
  ordinamento: Ordinamento | null;
  ordina: (chiave: string) => void;
} {
  const [ordinamento, setOrdinamento] = useState<Ordinamento | null>(null);
  const ordina = useCallback((chiave: string) => {
    setOrdinamento((attuale) => prossimoOrdinamento(attuale, chiave));
  }, []);
  return { ordinamento, ordina };
}

/**
 * Un'intestazione di colonna su cui si può cliccare per ordinare.
 *
 * ⚠️ **Un pulsante vero dentro il `th`, non un `onClick` sul `th`.** È la stessa
 * scelta già fatta per il nome del soggetto nell'elenco: la cella si raggiunge
 * col mouse, il pulsante anche con Tab e Invio. Un `th` cliccabile è invisibile
 * a chi naviga da tastiera.
 */
export function IntestazioneOrdinabile({
  chiave,
  ordinamento,
  onOrdina,
  className,
  children,
}: {
  chiave: string;
  ordinamento: Ordinamento | null;
  onOrdina: (chiave: string) => void;
  className?: string;
  children: ReactNode;
}) {
  const verso =
    ordinamento !== null && ordinamento.chiave === chiave ? ordinamento.verso : null;

  return (
    <th scope="col" className={className} aria-sort={ariaSort(ordinamento, chiave)}>
      <button
        type="button"
        className="intestazione-ordinabile"
        onClick={() => onOrdina(chiave)}
      >
        {children}
        {/*
          Il triangolino compare **solo** quando la colonna è ordinata: prima
          del primo clic l'intestazione deve restare identica a com'era.

          `aria-hidden` perché il verso è già annunciato da `aria-sort` sulla
          cella: senza, chi ascolta lo sentirebbe due volte, una delle quali
          come un carattere senza nome.
        */}
        {verso !== null && (
          <span className="segno-ordine" aria-hidden="true">
            {verso === 'salita' ? '▲' : '▼'}
          </span>
        )}
      </button>
    </th>
  );
}
